<!--
  A subject's local agents (a pull request's Overview card, the PR header's
  agent panel, a compare's page) (docs/superpowers/specs/2026-10-23-relay-pr-connections-design.md,
  "PR first" and "Manage and revoke"): the agents with a grant on it, with
  their fingerprint, presence and scope, to change or revoke (one its
  computer refused as refused, to grant again or revoke); and Connect
  my agent, which grants an agent already connected at once, with the
  scope chosen here, or makes a request with a string to paste
  into a new one, whose scope is chosen once, on its approval card, with
  the agent's fingerprint in view. Grants and pairings are this browser's
  (state/relayAgents.svelte.ts). An agent not heard from for an hour
  (relayAgents' `stale`) isn't offered to Connect my agent, and one that
  still holds a grant here says so; the one offered says when it was.
-->
<script lang="ts">
  import { copyText } from '../clipboard';
  import { MOCK } from '../mock/flag';
  import { showFingerprint, relay, type PendingRequest, type RelayCategory } from '../api/relay';
  import { KIND_NAMES, parseSubject, presetFor, type SubjectKey } from '../api/relaySubject';
  import { tick, untrack } from 'svelte';
  import { lastSeenText, pasteString, presenceText, scopeText } from '../logic/relayText';
  import { subjectPath } from '../router';
  import { relayAgents } from '../state/relayAgents.svelte';
  import RelayInstall from './RelayInstall.svelte';
  import { harnessText } from '../logic/relayHarness';
  import RelayRefused from './RelayRefused.svelte';
  import RelayScope from './RelayScope.svelte';
  /** `embedded`: shown inside another surface (the PR header's agent panel), without the card's frame and anchor. */
  /** `subject`: the subject's key (a pull request's or a compare's), whose grants it shows and makes. */
  let { subject, embedded = false }: { subject: SubjectKey; embedded?: boolean } = $props();
  // svelte-ignore state_referenced_locally
  const pr = subject;
  // svelte-ignore state_referenced_locally
  const at = parseSubject(subject)!;
  /** What the subject is called: "pull request", "compare". */
  const noun = KIND_NAMES[at.kind];
  const uid = $props.id();
  const why = (e: unknown) => (e instanceof Error ? e.message : String(e));

  const granted = $derived(
    relayAgents.grantsOn(pr).flatMap((g) => {
      const c = relayAgents.connection(g.connection);
      return c ? [{ g, c }] : [];
    }),
  );
  /** Paired agents without a grant here, which Connect my agent can grant at once: those heard from lately. */
  const ungranted = $derived(relayAgents.connections.filter((c) => !granted.some((x) => x.c.id === c.id)));
  const others = $derived(ungranted.filter((c) => !relayAgents.stale(c.id)));
  /** How many it leaves out, not heard from lately. */
  const unseen = $derived(ungranted.length - others.length);

  /** Whether an agent here reads the subject: its grant isn't refused. */
  const reading = $derived(granted.some((x) => x.g.status === 'granted'));

  /** Each granted agent's presence, asked of its computer once its grant is in force here (relayAgents.checkPresence, which the header's agent button shares). */
  const presence = $derived(relayAgents.presence);
  $effect(() => {
    // Asked again once the connection is back from down (checkPresence asks nothing while it is).
    void relayAgents.workerDown;
    const ids = granted.filter((x) => x.g.status === 'granted').map((x) => x.c.id);
    untrack(() => { for (const id of ids) relayAgents.checkPresence(id); });
  });

  /** A scope change on its way, shown until the grants are read again. */
  let pending = $state<Record<string, RelayCategory[]>>({});
  let busy = $state(false);
  let error = $state('');
  let news = $state('');
  let newsLine: HTMLElement | undefined = $state();
  let pasteBox: HTMLTextAreaElement | undefined = $state();
  /**
   * Runs `fn`, then says `done`. With `to`, focus then moves there: the
   * control used went away with what it acted on (a revoked agent, the
   * panel closed), so focus would otherwise fall to the page.
   */
  async function act(fn: () => Promise<void>, done: string | (() => string) = '', to?: () => HTMLElement | undefined) {
    if (busy) return;
    busy = true; error = ''; news = '';
    try {
      await fn();
      news = typeof done === 'string' ? done : done();
      if (to) { await tick(); to()?.focus(); }
    } catch (e) { error = why(e); } finally { busy = false; }
  }
  function setScope(connection: string, name: string, scope: RelayCategory[]) {
    pending[connection] = scope;
    void act(() => relayAgents.grant(connection, pr, scope), `${name} can now read ${scopeText(scope)}.`).finally(() => delete pending[connection]);
  }
  /** Sends a refused grant again as it was, then says what came of it (refused again, maybe) where focus goes. */
  function grantAgain(connection: string, name: string, scope: readonly RelayCategory[]) {
    void act(() => relayAgents.grant(connection, pr, [...scope]), () => {
      const g = relayAgents.grantsOn(pr).find((x) => x.connection === connection);
      return g?.status === 'refused' ? `${name}’s computer refused the grant again: ${g.reason}` : `${name} can now read ${scopeText(scope)}.`;
    }, () => newsLine);
  }
  /** The person says a just-connected agent is the one asked for; focus moves to the line saying so (the button goes). */
  async function looksRight(connection: string, name: string) {
    relayAgents.acknowledge(connection);
    news = `${name} is marked as the agent you asked to connect.`;
    await tick();
    newsLine?.focus();
  }
  function revoke(connection: string, name: string) {
    void act(() => relayAgents.revokeGrant(connection, pr), `${name} can no longer read this ${noun}.`, () => newsLine);
  }

  // Connect my agent.
  let open = $state(false);
  let scope = $state<RelayCategory[]>(presetFor(at.kind));
  let chosen = $state('');
  let request = $state<PendingRequest | null>(null);
  let copied = $state(false);
  $effect(() => { if (!others.some((c) => c.id === chosen)) chosen = others[0]?.id ?? ''; });
  const paste = $derived(request ? pasteString(`${location.origin}${subjectPath(at)}`, request.req, at.kind === 'pull' ? 'PR' : noun) : '');
  function close() { open = false; request = null; copied = false; }
  function grantChosen() {
    const c = relayAgents.connection(chosen);
    if (!c || busy) return;
    void act(async () => { await relayAgents.grant(c.id, pr, scope); open = false; }, `${c.name} can now read this ${noun}.`, () => newsLine);
  }
  /** Disconnects the agent chosen in the picker, from every subject, as Settings does; one whose computer can't be reached is forgotten there. */
  function disconnectChosen() {
    const c = relayAgents.connection(chosen);
    if (!c || busy) return;
    if (!confirm(`Disconnect ${c.name} and delete its task history? It can no longer read anything here, and you will need a new link from your agent to reconnect.`)) return;
    void act(() => relayAgents.unpair(c.id), `Disconnected ${c.name}.`, () => newsLine);
  }
  function newAgent() {
    // The kind's preset: the approval card presets it, and the person chooses there.
    void act(async () => { request = await relay.createRequest(pr, presetFor(at.kind)); copied = false; }, '', () => pasteBox);
  }
  async function copy() {
    if (await copyText(paste)) copied = true;
    else error = 'Select and copy the text above.';
  }
</script>

<section id={embedded ? undefined : 'card-agents'} class="relay-agents" class:side-card={!embedded} aria-labelledby="{uid}-h">
  <h3 id="{uid}-h">Local agents</h3>
  {#if MOCK}
    <p class="muted small">Agent connections are available in a signed-in Changeling instance.</p>
  {:else}
    {#if !reading && relayAgents.loaded}<p class="muted small">No agent can read this {noun}.</p>{/if}
    {#if granted.length}
      <ul class="agents">
        {#each granted as { g, c } (c.id)}
          {@const p = presence[c.id]}
          <li>
            <div class="who">
              <b>{c.name}</b>
              <span class="muted small">{harnessText(c.harness)} · <span class="mono">{showFingerprint(c.peer)}</span></span>
              {#if g.status !== 'refused'}<span class="muted small">{p === undefined || p === 'checking' ? 'Checking…' : presenceText(p.presence, p.reachable)}</span>{/if}
            </div>
            <!-- Its presence, once asked, is fresher than last seen (the worker writes that a minute apart). -->
            {#if relayAgents.stale(c.id) && !(p !== undefined && p !== 'checking' && p.reachable && p.presence !== 'offline')}
              <p class="stale small">{lastSeenText(relayAgents.lastSeen(c.id), relayAgents.now)}. It can still read this {noun}: revoke it if you’re done with it.</p>
            {/if}
            {#if relayAgents.connected.includes(c.id)}<p class="just small">Just connected. If this isn’t the agent you asked to connect, revoke it. <button class="linkish" onclick={() => looksRight(c.id, c.name)}>Looks right</button></p>{/if}
            {#if g.status === 'refused'}
              <RelayRefused grant={g} label="Grant {c.name} again" disabled={busy} onretry={() => grantAgain(c.id, c.name, g.scope)} />
            {:else}
              <RelayScope kind={at.kind} legend="What {c.name} may read" scope={pending[c.id] ?? g.scope} disabled={busy} onchange={(s) => setScope(c.id, c.name, s)} />
            {/if}
            <button class="btn small danger" aria-label="Revoke {c.name}" aria-disabled={busy ? 'true' : undefined} onclick={() => revoke(c.id, c.name)}>Revoke</button>
          </li>
        {/each}
      </ul>
    {/if}

    <button class="btn small" aria-expanded={open} aria-controls="{uid}-connect" onclick={() => (open ? close() : (open = true))}>Connect my agent</button>
    {#if open}
      <div class="connect" id="{uid}-connect" role="group" aria-label="Connect my agent">
        {#if !request}
          {#if others.length}
            <div class="grant-other">
              <label>Agent already connected
                <select value={chosen} aria-disabled={busy ? 'true' : undefined} aria-describedby="{uid}-seen" onchange={(e) => { if (busy) e.currentTarget.value = chosen; else chosen = e.currentTarget.value; }}>
                  {#each others as c (c.id)}<option value={c.id}>{c.name} · {showFingerprint(c.peer)}</option>{/each}
                </select>
              </label>
              <span class="muted small seen" id="{uid}-seen">{lastSeenText(relayAgents.lastSeen(chosen), relayAgents.now)}</span>
            </div>
            <!-- An agent already connected is granted here and now, with no card to follow: its scope is chosen here. -->
            <RelayScope kind={at.kind} legend="It may read" {scope} disabled={busy} onchange={(s) => (scope = s)} />
            <div class="grant-actions">
              <button class="btn small primary" aria-disabled={busy || !chosen ? 'true' : undefined} onclick={grantChosen}>Grant</button>
              <button class="btn small danger" aria-disabled={busy || !chosen ? 'true' : undefined} onclick={disconnectChosen}>Disconnect…</button>
            </div>
          {/if}
          {#if unseen}<p class="muted small">{unseen === 1 ? '1 agent' : `${unseen} agents`} not heard from in the last hour {unseen === 1 ? 'isn’t' : 'aren’t'} listed. <a href="/settings">Manage them in Settings</a>.</p>{/if}
          <button class="btn small" class:primary={!others.length} aria-disabled={busy ? 'true' : undefined} onclick={newAgent}>Connect a new agent</button>
        {:else}
          <label class="paste">Paste this into your agent
            <textarea readonly rows="3" value={paste} bind:this={pasteBox}></textarea>
          </label>
          <button class="btn small primary" onclick={copy}>{copied ? 'Copied' : 'Copy'}</button>
          <span class="sr-only" role="status">{copied ? 'Copied' : ''}</span>
          <p class="muted small">Request <span class="mono">{request.req}</span> is good for ten minutes and grants nothing by itself: when your agent asks, you check its fingerprint and choose what it may read here.</p>
          <details>
            <summary>Install the plugin</summary>
            <RelayInstall />
          </details>
        {/if}
        {#if request}<button class="linkish" onclick={close}>Done</button>{/if}
      </div>
    {/if}
    {#if relayAgents.error}<p class="setting-problem small" role="alert">{relayAgents.error}</p>{/if}
    {#if error}<p class="setting-problem small" role="alert">{error}</p>{/if}
    <p class="muted small news" role="status" tabindex="-1" bind:this={newsLine}>{news}</p>
  {/if}
</section>

<style>
  .relay-agents { display: grid; gap: 10px; }
  .agents { list-style: none; margin: 0; padding: 0; display: grid; gap: 12px; }
  .agents li { display: grid; gap: 8px; justify-items: start; padding-top: 10px; border-top: 1px solid var(--line); }
  .agents li:first-child { padding-top: 0; border-top: 0; }
  .who { display: grid; gap: 1px; min-width: 0; }
  .who > * { overflow-wrap: anywhere; }
  .connect { display: grid; gap: 10px; justify-items: start; padding: 10px 12px; background: var(--code); border-radius: 8px; }
  .grant-other { display: flex; gap: 4px 10px; align-items: end; flex-wrap: wrap; }
  .grant-other .seen { line-height: 28px; }
  .grant-actions { display: flex; gap: 6px; flex-wrap: wrap; }
  label { display: grid; gap: 4px; font-size: 12px; color: var(--muted); }
  select { padding: 5px 8px; font-size: 13px; max-width: 100%; }
  select[aria-disabled='true'] { opacity: .55; cursor: default; }
  .paste { justify-self: stretch; }
  textarea { min-height: 0; font: 12px/1.5 ui-monospace, SFMono-Regular, monospace; color: var(--fg); }
  details { font-size: 12px; justify-self: stretch; }
  details > :global(.relay-install) { margin-top: 8px; }
  p { margin: 0; }
  .news:empty { display: none; }
  .stale { color: var(--muted); }
  .just { padding: 6px 8px; border-radius: 6px; background: var(--warnbg); color: var(--fg); }
  .btn[aria-disabled='true'] { opacity: .45; cursor: default; }
</style>
